import { isAxiosError } from "../helpers";
import type {
	MappedHttpRequest,
	ErrorRequest,
	AxiosErrorRequest,
	ErrorUtilityMethods,
	ErrorDetails,
	ErrorRequestContext,
} from "./types";
import { WrappedError } from "./types";
import type { AxiosError } from "axios";

export abstract class BaseErrorUtility<TRequest>
	implements ErrorUtilityMethods<TRequest>
{
	constructor(
		protected readonly projectName: string,
		protected readonly apiKey: string,
		protected readonly debug?: boolean,
	) {
		if (!projectName.length) {
			throw new Error("Invalid project name");
		}

		if (!apiKey.length) {
			throw new Error("Invalid API key");
		}
	}

	protected abstract mapRequest(
		request: TRequest,
	): Promise<MappedHttpRequest> | MappedHttpRequest;

	async sendError(
		error: Error | unknown,
		userContext?: Record<string, unknown>,
	) {
		try {
			const payload = await this.mapErrorRequestPayload(
				error,
				undefined,
				userContext,
			);

			return this.send(payload);
		} catch (err) {
			if (this.debug) {
				console.log(err);
			}
		}
	}

	/**
	 * Sends error and request details to try-catch-cloud service.
	 * You can manually map details from your route to request object.
	 *
	 * ```
	 *  errorTrack.sendErrorFromEndpoint(
	 *      error,
	 *      {
	 *          url: req.originalUrl.split("?")[0],
	 *          method: req.method,
	 *          headers: req.headers,
	 *          query: req.query,
	 *          body,
	 *      },
	 *      { ...context }
	 *  );
	 * ```
	 */
	async sendErrorFromEndPoint(
		error: Error | unknown,
		request: TRequest,
		userContext?: Record<string, unknown>,
	) {
		try {
			const payload = await this.mapErrorRequestPayload(
				error,
				request,
				userContext,
			);

			return this.send(payload);
		} catch (err) {
			if (this.debug) {
				console.log(err);
			}
		}
	}

	protected async send(payload: ErrorRequest): Promise<{ id: string }> {
		const result = await fetch("https://oleksiikilevoi.site/api/err-log/new", {
			method: "POST",
			body: JSON.stringify(payload),
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (this.debug) {
			console.info("Send result", await result.text());
		}

        const data: { id: string } = await result.json();

        return data;
	}

	/**
	 * It has to return Promise to be compatible with different implementations
	 * e.g. Hono returns Promise when accessing req.json()
	 */
	protected async mapErrorRequestPayload(
		error: Error | unknown,
		request?: TRequest,
		context?: Record<string, unknown>,
	): Promise<ErrorRequest> {
		if (!(error instanceof Error)) {
			return this.mapErrorRequestPayload(
				new WrappedError("An unexpected value was thrown", error),
				request,
				context,
			);
		}

		if (isAxiosError(error)) {
			return this.mapAxiosError(error, request, context);
		}

		const { stack, ...restErrorDetails } = error;

		return {
			...(request ? await this.mapRequest(request) : {}),
			errorName: error.name,
			errorDetails: this.sanitizeValue({
				...restErrorDetails,
				name: error.name,
				message: error.message,
			}) as ErrorDetails,
			stack: error.stack ?? null,
			context: this.sanitizeValue(context ?? null) as ErrorRequestContext,
			user: this.apiKey,
			projectName: this.projectName,
		};
	}

	protected mapAxiosRequestBody(error: AxiosError) {
		try {
			const config = error.config;
			const headers: Record<string, string> = config?.headers ?? {};
			const contentType: string =
				headers["content-type"] ?? headers["Content-Type"] ?? "";

			if (!contentType.length) {
				return null;
			}

			if (contentType.startsWith("multipart/form-data")) {
				return "#form-data";
			}

			if (contentType === "application/json") {
				return JSON.parse(config?.data);
			}

			if (contentType.match(/^text\/.+$/)) {
				return config?.data as string;
			}

			if (contentType === "application/x-www-form-urlencoded") {
				return new URLSearchParams(config?.data).toString();
			}

			if (contentType === "application/xml") {
				return decodeURIComponent(config?.data); // Assuming data is already a string
			}

			if (contentType === "application/octet-stream") {
				return "#binary-data";
			}

			return "#tcc/mapping-failed";
		} catch (e) {
			if (this.debug) {
				console.error(e);
			}

			return "#tcc/mapping-failed";
		}
	}

	protected async mapAxiosError(
		error: AxiosError,
		request?: TRequest,
		context?: ErrorRequestContext,
	): Promise<AxiosErrorRequest> {
		const { config, response } = error;
		const requestBody = this.mapAxiosRequestBody(error);
		const responseBody = response?.data;

		const rawUrl = config?.url ?? "";
		const query = Object.fromEntries([
			...(URL.canParse(rawUrl) ? new URL(rawUrl).searchParams.entries() : []),
		]);

		const axiosDetails = {
			url: config?.url ?? null,
			query: Object.keys(query).length ? query : null,
			params: config?.params ?? null,
			method: config?.method?.toUpperCase() || null,
			statusCode: response?.status ?? 0,
			requestBody,
			responseBody: responseBody ?? null,
			headers: config?.headers ?? {},
		};

		return {
			...(request ? await this.mapRequest(request) : {}),
			errorName: error.constructor.name ?? error.name,
			errorDetails: this.sanitizeValue({
				...axiosDetails,
				name: error.name,
				message: error.message,
			}) as ErrorDetails<typeof axiosDetails>,
			stack: error.stack ?? null,
			context: context ?? null,
			user: this.apiKey,
			projectName: this.projectName,
		};
	}

	protected sanitizeValue(value: unknown, level = 1): unknown {
		if (typeof value === "object" && value !== null && !Array.isArray(value)) {
			const result: Record<string, unknown> = {};

			for (const [key, objValue] of Object.entries(value)) {
				result[key] = this.sanitizeValue(objValue, level + 1);
			}

			return result;
		}

		if (typeof value === "object" && Array.isArray(value)) {
			return value.map((item) => this.sanitizeValue(item, level + 1));
		}

		if (typeof value === "function") {
			return undefined;
		}

		return value;
	}
}
